<template>
    <div v-if="!loading && filters?.length">
        <!-- <PagesCategoryFiltersTypeFilter v-if="typeFilter" :filter="typeFilter" /> -->
        <Separator />
        <div class="flex justify-between max-lg:px-1 py-4 overflow-x-auto">
            <div class="flex gap-4">
                <div class="flex items-center">
                    <InputGroup>
                        <InputGroupInput v-model="nameFilter" type="text" :placeholder="$t('category.search_by_name')" />
                        <InputGroupAddon>
                            <Search />
                        </InputGroupAddon>
                    </InputGroup>
                </div>
                <PageCategoriesFiltersFilterItem
                    v-for="(filter, key) in filtersList"
                    :key
                    :filter
                    :active="attributeFilters[key]"
                    @update:filter="updateFilter"
                />
            </div>
            <Transition name="slide-fade">
                <Button v-if="activeFilters?.attributes?.length" class="text-xs" variant="outline" @click="clearAllFilters">
                    <Icon name="mingcute:close-line" />
                    {{ $t("category.filters.clear") }}
                </Button>
            </Transition>
        </div>
        <Separator />
    </div>
</template>

<script lang="ts" setup>
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import type { APICategoryAttributes } from "~~/shared/types/category";
import { Search } from "@lucide/vue";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";

export interface ActiveFilterItem {
    name: string;
    value?: string | string[];
}

export interface ActiveFilter {
    name: string;
    attributes: ActiveFilterItem[];
}

interface Props {
    filters?: APICategoryAttributes[] | null;
    loading: boolean;
}

const activeFilters = defineModel({ type: Object as PropType<ActiveFilter>, required: false });

const emits = defineEmits(["update:filters"]);

const props = defineProps<Props>();

const filtersList = computed(() => {
    return useFilter(
        props.filters,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (el: any) => el.name != "type" && (el.type === "multi-select" || el.type === "dropdown" || el.type === "boolean"),
    );
});

const computedActiveFilters = computed({
    get() {
        return activeFilters.value;
    },
    set(v: { type: keyof ActiveFilter; value?: ActiveFilterItem | string }) {
        const type = v.type;
        const value = v.value;

        // Ensure root object exists
        if (!activeFilters.value) {
            activeFilters.value = { name: "", attributes: [] } as ActiveFilter;
        }

        // Handle setting the `name` field
        if (type === "name") {
            const nameVal = typeof value === "string" ? value : "";

            // Assign a new object to avoid setting property on undefined
            activeFilters.value = {
                ...(activeFilters.value ?? { name: "", attributes: [] }),
                name: nameVal,
            } as ActiveFilter;

            emits("update:filters");
            return;
        }

        // Handle `attributes` array
        if (type === "attributes") {
            const incoming = value as ActiveFilterItem | undefined;

            // Work on a local copy first to avoid mutating undefined
            const currentAttrs = Array.isArray(activeFilters.value?.attributes) ? [...activeFilters.value!.attributes] : [];

            const idx = incoming ? currentAttrs.findIndex((f) => f.name === incoming.name) : -1;

            if (!incoming || !incoming.value || (Array.isArray(incoming.value) && incoming.value.length === 0)) {
                if (idx !== -1) currentAttrs.splice(idx, 1);
            } else {
                if (idx !== -1) currentAttrs[idx] = incoming;
                else currentAttrs.push(incoming);
            }

            // Assign the attributes array back to the activeFilters object
            activeFilters.value = {
                ...(activeFilters.value ?? { name: "", attributes: [] }),
                attributes: currentAttrs,
            } as ActiveFilter;

            emits("update:filters");
            return;
        }
    },
});

// { filter: { attributes: filters } }
const attributeFilters = computed({
    get() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return filtersList.value.map((filter: any) => {
            const found = activeFilters.value?.attributes.find((f) => f.name === filter.name);
            return found ? found : { name: filter.name, value: undefined };
        });
    },
    set(value: ActiveFilterItem) {
        computedActiveFilters.value = { type: "attributes", value };
    },
});

const nameFilter = computed({
    get() {
        return activeFilters.value?.name;
    },
    set(value: string) {
        computedActiveFilters.value = { type: "name", value };
    },
});

const updateFilter = (v: ActiveFilterItem) => {
    attributeFilters.value = v;
};

// const typeFilter = computed(() => {
//     return useFind(props.filters, { name: "type" });
// });

const clearAllFilters = () => {
    activeFilters.value = undefined;
    emits("update:filters");
};
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(15px);
    opacity: 0;
}
</style>
